/**
 * Utility functions for validating and formatting Chilean phone numbers (Mobile and Landline)
 * and international phone numbers.
 */

export interface PhoneValidationResult {
  isValid: boolean;
  clean: string;
  formatted: string;
  type: 'celular' | 'fijo' | 'internacional' | 'desconocido';
  error?: string;
}

// Regex patterns
const CHILE_MOBILE_REGEX = /^(\+?56)?9\d{8}$/;
const CHILE_LANDLINE_REGEX = /^(\+?56)?[2-7]\d{7,8}$/;

export function validatePhone(phoneString: string): PhoneValidationResult {
  const raw = phoneString.trim();
  if (!raw) {
    return {
      isValid: false,
      clean: '',
      formatted: '',
      type: 'desconocido',
      error: 'El número de teléfono es requerido.',
    };
  }

  // Remove spaces, hyphens, parenthesis, dots
  let digitsOnly = raw.replace(/[\s\-\(\)\.]/g, '');

  // Normalize leading 00 to +
  if (digitsOnly.startsWith('00')) {
    digitsOnly = '+' + digitsOnly.slice(2);
  }

  // If user entered +56... or 56...
  let normalized = digitsOnly;
  if (!normalized.startsWith('+') && !normalized.startsWith('56')) {
    // If it starts with 9 and has 9 digits or starts with 2-7
    // assume user entered number without +56 prefix
  } else if (normalized.startsWith('56') && !normalized.startsWith('+')) {
    normalized = '+' + normalized;
  }

  // Case 1: Chilean Mobile (Starts with 9, exactly 9 digits after +56)
  // Check if normalized matches +569XXXXXXXX or plain 9XXXXXXXX
  const cleanMobileDigits = normalized.replace(/^\+?56/, '');
  if (/^9\d{8}$/.test(cleanMobileDigits)) {
    const mobileDigits = cleanMobileDigits.slice(1);
    return {
      isValid: true,
      clean: `+569${mobileDigits}`,
      formatted: `+56 9 ${mobileDigits.slice(0, 4)} ${mobileDigits.slice(4)}`,
      type: 'celular',
    };
  }

  // Case 2: Chilean Landline (Starts with 2..7, 8 or 9 digits after +56)
  const cleanLandlineDigits = normalized.replace(/^\+?56/, '');
  if (/^[2-7]\d{7,8}$/.test(cleanLandlineDigits)) {
    if (cleanLandlineDigits.length === 9) {
      const areaCode = cleanLandlineDigits[0];
      const rest = cleanLandlineDigits.slice(1);
      return {
        isValid: true,
        clean: `+56${cleanLandlineDigits}`,
        formatted: `+56 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4)}`,
        type: 'fijo',
      };
    } else {
      // 8 digits legacy
      const areaCode = cleanLandlineDigits[0];
      const rest = cleanLandlineDigits.slice(1);
      return {
        isValid: true,
        clean: `+56${cleanLandlineDigits}`,
        formatted: `+56 ${areaCode} ${rest.slice(0, 3)} ${rest.slice(3)}`,
        type: 'fijo',
      };
    }
  }

  // Case 3: International number starting with + (other than +56)
  if (normalized.startsWith('+') && !normalized.startsWith('+56')) {
    const internationalDigits = normalized.slice(1);
    if (/^\d{7,15}$/.test(internationalDigits)) {
      return {
        isValid: true,
        clean: normalized,
        formatted: normalized,
        type: 'internacional',
      };
    } else {
      return {
        isValid: false,
        clean: normalized,
        formatted: raw,
        type: 'internacional',
        error: 'El teléfono internacional debe contener entre 7 y 15 dígitos.',
      };
    }
  }

  // If user typed something starting with 9 but wrong length
  if (cleanMobileDigits.startsWith('9')) {
    return {
      isValid: false,
      clean: raw,
      formatted: raw,
      type: 'celular',
      error: 'El teléfono celular debe tener 9 dígitos en total (ej: 9 8765 4321).',
    };
  }

  // If user typed something starting with 2-7 but wrong length
  if (/^[2-7]/.test(cleanLandlineDigits)) {
    return {
      isValid: false,
      clean: raw,
      formatted: raw,
      type: 'fijo',
      error: 'El teléfono fijo debe tener 8 o 9 dígitos en total (ej: 2 2123 4567).',
    };
  }

  return {
    isValid: false,
    clean: raw,
    formatted: raw,
    type: 'desconocido',
    error: 'Ingresa un número telefónico chileno válido (ej: 9 8765 4321 o 2 2123 4567) o con prefijo internacional.',
  };
}
